import React, { useState, lazy, Suspense, useEffect, useRef, useCallback } from "react";
import LandingNav from "../../components/Util/Nav/LandingNav";
import HeroImage from "../../components/Landing/HeroImage";
import "./LandingPage.css";
import { useResponsive } from "../../components/Landing/hooks/useResponsive";
import Skeleton, { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { motion, AnimatePresence } from "framer-motion";

const AboutSection = lazy(() => import("../../components/Landing/AboutSection"));

// Priority load - important but not critical
const EverythingYouNeedSection = lazy(() => 
  import(/* webpackChunkName: "eyn", webpackPrefetch: true */ "../../components/Landing/EYNSection").then(module => ({
    default: module.default
  }))
);

// Lazy load below-the-fold components with lower priority
const ThreeStepSection = lazy(() => 
  import(/* webpackChunkName: "below-fold", webpackPrefetch: true */ "../../components/Landing/ThreeStepSection")
);
const LandingFooter = lazy(() => 
  import(/* webpackChunkName: "below-fold", webpackPrefetch: true */ "../../components/Util/Footer/LandingFooter")
);

// Import optimized components - lowest priority
const TestimonialSection = lazy(() => 
  import(/* webpackChunkName: "testimonials", webpackPrefetch: true */ "../../components/Landing/TestimonialSection")
);
const FAQSection = lazy(() => 
  import(/* webpackChunkName: "faq", webpackPrefetch: true */ "../../components/Landing/FAQSection")
);
const IntegrationsSection = lazy(() => 
  import(/* webpackChunkName: "integrations", webpackPrefetch: true */ "../../components/Landing/IntegrationsSection")
);
const TransformSection = lazy(() => 
  import(/* webpackChunkName: "transform", webpackPrefetch: true */ "../../components/Landing/TransformSection")
);

// Enhanced intersection observer hook for performance monitoring
const useOptimizedIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const ref = useRef(null);
  const observerRef = useRef(null);

  useEffect(() => {
    const currentRef = ref.current;
    if (!currentRef || hasIntersected) return;

    observerRef.current = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
      if (entry.isIntersecting && !hasIntersected) {
        setHasIntersected(true);
        observerRef.current?.disconnect();
      }
    }, {
      threshold: 0,
      rootMargin: '600px',
      ...options
    });

    observerRef.current.observe(currentRef);

    return () => {
      observerRef.current?.disconnect();
    };
  }, [hasIntersected, options]);

  return [ref, isIntersecting, hasIntersected];
};

// Enhanced loading component with skeleton
const OptimizedSectionLoader = ({ height = "300px", section = "default" }) => {
  const getSkeletonContent = () => {
    switch (section) {
      case "hero":
        return (
          <div className="container-fluid p-0">
            <div className="row mx-0">
              <div className="col px-0">
                <div className="d-flex flex-column align-items-center mt-3">
                  <Skeleton style={{width: 210, height: 60, borderRadius: 12}}/>
                  <div className="w-75 mx-5 my-1 text-center">
                    <Skeleton className="container" style={{height: "145px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "210px", borderRadius: 12}}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "about":
        return (
          <div className="container-fluid p-0">
            <div className="row mx-0">
              <div className="col px-0">
                <div className="d-flex flex-column align-items-center mt-3">
                  <Skeleton style={{width: 210, height: 60, borderRadius: 12}}/>
                  <div className="w-75 mx-5 my-1 text-center">
                    <Skeleton className="container" style={{height: "145px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "210px", borderRadius: 12}}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "eyn":
        return (
          <div className="container-fluid p-0">
            <div className="row mx-0">
              <div className="col px-0">
                <div className="d-flex flex-column align-items-center mt-3">
                  <Skeleton style={{width: 210, height: 60, borderRadius: 12}}/>
                  <div className="w-75 mx-5 my-1 text-center">
                    <Skeleton className="container" style={{height: "145px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "210px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "142px", borderRadius: 12}}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case "stats":
        return (
          <div className="container-fluid p-0">
            <div className="row mx-0">
              <div className="col px-0">
                <div className="d-flex flex-column align-items-center mt-3">
                  <Skeleton style={{width: 210, height: 60, borderRadius: 12}}/>
                  <div className="w-75 mx-5 my-1 text-center">
                    <Skeleton className="container" style={{height: "145px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "210px", borderRadius: 12}}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return (
          <div className="container-fluid p-0">
            <div className="row mx-0">
              <div className="col px-0">
                <div className="d-flex flex-column align-items-center mt-3">
                  <Skeleton style={{width: 210, height: 60, borderRadius: 12}}/>
                  <div className="w-75 mx-5 my-1 text-center">
                    <Skeleton className="container" style={{height: "145px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "210px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "142px", borderRadius: 12}}/>
                  </div>
                  <div className="mt-3 w-75 text-center">
                    <Skeleton className="container" style={{height: "70px", borderRadius: 12}}/>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div style={{ minHeight: height, width: '100%' }}>
      <SkeletonTheme baseColor="#2F363D" highlightColor="#2A9D8F">
        {getSkeletonContent()}
      </SkeletonTheme>
    </div>
  );
};

const fadeInVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.45, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

const FadeInSection = ({ children }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.06, margin: "0px 0px -60px 0px" }}
    variants={fadeInVariants}
  >
    {children}
  </motion.div>
);

// Prefetch near viewport, fade in on scroll — no sticky min-height after load
const LazySection = ({
  children,
  fallback,
  priority = "low",
  height = "200px",
  className = "",
}) => {
  const prefetchMargin =
    priority === "high" ? "500px" : priority === "medium" ? "400px" : "300px";

  const [sectionRef, isIntersecting, hasIntersected] =
    useOptimizedIntersectionObserver({
      rootMargin: prefetchMargin,
    });

  const shouldLoad = priority === "high" || hasIntersected || isIntersecting;

  return (
    <div
      ref={sectionRef}
      className={`landing-section ${className}`.trim()}
      style={shouldLoad ? undefined : { minHeight: height }}
    >
      {shouldLoad ? (
        <Suspense
          fallback={fallback || <OptimizedSectionLoader height={height} />}
        >
          <FadeInSection>{children}</FadeInSection>
        </Suspense>
      ) : (
        fallback || <OptimizedSectionLoader height={height} />
      )}
    </div>
  );
};

const LandingPage = () => {
  const [lightMode, setLightMode] = useState(false);
  const { isLgUp } = useResponsive();
  const [performanceMode, setPerformanceMode] = useState('auto');
  
  // Performance monitoring (like Shopify does)
  const performanceRef = useRef({
    navigationStart: performance.now(),
    sectionsLoaded: 0,
    isSlowConnection: false,
    isSlowDevice: false
  });

  const toggleLightMode = useCallback(() => setLightMode((m) => !m), []);

  // Detect performance constraints
  useEffect(() => {
    // Detect slow connection
    if ('connection' in navigator) {
      const connection = navigator.connection;
      performanceRef.current.isSlowConnection = 
        connection.effectiveType === '2g' || 
        connection.effectiveType === 'slow-2g' ||
        connection.downlink < 1.5;
    }

    // Detect slow device
    const checkDevicePerformance = () => {
      const start = performance.now();
      setTimeout(() => {
        const elapsed = performance.now() - start;
        performanceRef.current.isSlowDevice = elapsed > 20;
        
        // Set performance mode based on constraints
        if (performanceRef.current.isSlowConnection || performanceRef.current.isSlowDevice) {
          setPerformanceMode('conservative');
        } else {
          setPerformanceMode('optimized');
        }
      }, 0);
    };

    checkDevicePerformance();

    // Also check hardware
    if (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2) {
      performanceRef.current.isSlowDevice = true;
      setPerformanceMode('conservative');
    }
  }, []);

  // Preload critical resources (Shopify technique)
  useEffect(() => {
    if (performanceMode === 'optimized') {
      // Preload critical images and fonts
      const criticalResources = [
        // Add your critical image URLs here
      ];
      
      criticalResources.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = url;
        document.head.appendChild(link);
      });
    }
  }, [performanceMode]);

  // Prefetch below-the-fold modules in the background so content is ready even without scrolling
  useEffect(() => {
    const startPrefetch = () => {
      // Keep it light on conservative mode
      if (performanceMode === 'conservative') return;
      setTimeout(() => {
        import("../../components/Landing/EYNSection");
        import("../../components/Landing/StatsSection");
        import("../../components/Landing/NFSection");
        import("../../components/Landing/ThreeStepSection");
        import("../../components/Landing/IntegrationsSection");
        import("../../components/Landing/TestimonialSection");
        import("../../components/Landing/FAQSection");
        import("../../components/Landing/TransformSection");
        import("../../components/Landing/CTASection");
        import("../../components/Util/Footer/LandingFooter");
      }, 100);
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(startPrefetch, { timeout: 2000 });
    } else {
      setTimeout(startPrefetch, 300);
    }
  }, [performanceMode]);


  // Clear any stored tags
  useEffect(() => {
  localStorage.removeItem("tag");
  }, []);

  // Get optimized fallback height based on performance mode
  const getFallbackHeight = (section) => {
    const heights = {
      auto: {
        hero: '500px',
        about: '350px',
        eyn: '600px',
        default: '300px'
      },
      conservative: {
        hero: '400px',
        about: '300px',
        eyn: '500px',
        default: '250px'
      },
      optimized: {
        hero: '600px',
        about: '400px',
        eyn: '700px',
        default: '350px'
      }
    };
    
    // Fallback to 'auto' mode if performanceMode is not recognized
    const mode = heights[performanceMode] ? performanceMode : 'auto';
    return heights[mode]?.[section] || heights[mode].default;
  };

  return (
    <div className={lightMode ? "fullpage-white" : "bg-black"}>
      {/* Add resource hints for better performance */}
      <link rel="preconnect" href="https://lottie.host" />
      <link rel="preconnect" href="https://firebasestorage.googleapis.com" />
      
      <div className="blur-top-left" />
      <div className="blur-left" style={{ overflow: "hidden" }} />
      
      <LandingNav
        onToggleLightMode={toggleLightMode}
        mlp={isLgUp}
        style={{ marginTop: "0" }}
        isLandingPage={true}
      />
      
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
          className="landing-main"
        >
        <div className="container-fluid p-0" style={{ overflowX: "hidden" }}>
          <div className="row mx-0">
            <div className="col px-0">
              <div className="landing-hero-shell">
                <HeroImage lightMode={lightMode} />
              </div>

              <LazySection
                priority="high"
                height={getFallbackHeight("about")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("about")}
                    section="about"
                  />
                }
              >
                <AboutSection lightMode={lightMode} />
              </LazySection>

              <LazySection
                priority="medium"
                height={getFallbackHeight("eyn")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("eyn")}
                    section="eyn"
                  />
                }
              >
                <EverythingYouNeedSection lightMode={lightMode} />
              </LazySection>
              
              {/* <LazySection 
                priority="medium" 
                height={getFallbackHeight('stats')}
                fallback={<OptimizedSectionLoader height={getFallbackHeight('stats')} section="stats" />}
                animationType="default"
                delay={0.4}
              >
                <StatsSection lightMode={lightMode} />
              </LazySection> */}
              
              {/* Below-the-fold content - low priority */}
              {/* <LazySection 
                priority="low" 
                height={isMobile ? "0px" : getFallbackHeight('default')}
                fallback={isMobile ? null : <OptimizedSectionLoader height={getFallbackHeight('default')} section="default" />}
                animationType="default"
                delay={0.5}
                className={isMobile ? "d-none" : ""}
              >
                <NeverFaceSection lightMode={lightMode} />
              </LazySection> */}
              
              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <ThreeStepSection lightMode={lightMode} mlp={isLgUp} />
              </LazySection>

              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <IntegrationsSection lightMode={lightMode} />
              </LazySection>

              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <TestimonialSection lightMode={lightMode} />
              </LazySection>

              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <FAQSection lightMode={lightMode} />
              </LazySection>

              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <TransformSection lightMode={lightMode} />
              </LazySection>

              <LazySection
                priority="low"
                height={getFallbackHeight("default")}
                fallback={
                  <OptimizedSectionLoader
                    height={getFallbackHeight("default")}
                    section="default"
                  />
                }
              >
                <LandingFooter lightMode={lightMode} />
              </LazySection>
            </div>
          </div>
        </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default LandingPage;   